import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Table,
    CardBody,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button
} from 'reactstrap'
import { Spinner } from 'react-bootstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import CreateEwayBillModal from './Modals/CreateEwayBillModal'

// --- Helper: format money ---
const money = n =>
    (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

// --- Helper: format date/time ---
const formatDateTime = iso => {
    if (!iso) return '-'
    try {
        const d = new Date(iso)
        return d.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    } catch {
        return iso
    }
}

const ViewCRN = () => {
    const token =
        typeof window !== 'undefined' ? localStorage.getItem('token') : null

    // if you ever want to filter by store, you can use this param
    const { storeid } = useParams()

    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const [openId, setOpenId] = useState('')

    // cancel modal states
    const [cancelModalOpen, setCancelModalOpen] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [cancelInvoiceId, setCancelInvoiceId] = useState(null)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [cancelError, setCancelError] = useState(null)

    // convert to e-invoice modal states
    const [convertModalOpen, setConvertModalOpen] = useState(false)
    const [convertInvoiceNumber, setConvertInvoiceNumber] = useState('')
    const [convertInvoiceId, setConvertInvoiceId] = useState(null)
    const [convertLoading, setConvertLoading] = useState(false)
    const [convertError, setConvertError] = useState(null)
    const [pageSize, setPageSize] = useState(10)
    const [page, setPage] = useState(1)

    // e-way bill modal states
    const [ewayBillModalOpen, setEwayBillModalOpen] = useState(false)
    const [selectedInvoiceForEwayBill, setSelectedInvoiceForEwayBill] = useState(null)

    // sync tally state: { [crnId]: { loading: bool, error: string|null, success: string|null } }
    const [syncTallyState, setSyncTallyState] = useState({})

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 1000)
        return () => clearTimeout(t)
    }, [search])

    const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize))

    // reset to page 1 when debounced search or page size changes
    useEffect(() => { setPage(1) }, [debouncedSearch, pageSize])
    // clamp page if results shrink
    useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])

    const paginated = useMemo(() => {
        const start = (page - 1) * pageSize
        return invoices.slice(start, start + pageSize)
    }, [invoices, page, pageSize])

    const pageNums = () => {
        const delta = 2
        const start = Math.max(1, page - delta)
        const end = Math.min(totalPages, page + delta)
        return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    }
    const handleDownloadCRN = async (inv, copy) => {
        const gstReq = inv.gstzenRequestPayload || {}
        const seller = gstReq.SellerDtls || {}
        const buyer = gstReq.BuyerDtls || {}
        const ship = gstReq.ShipDtls || gstReq.DispDtls || {}
        const val = gstReq.ValDtls || {}
        const items = Array.isArray(gstReq.ItemList) ? gstReq.ItemList : []
        // Use backend-stored values directly — do NOT recalculate on frontend
        const totalAmount = Number(val?.AssVal || inv?.taxableValue || 0)
        const pickItems = inv.pickList?.lineItems || []
        const hasDiscount = pickItems.some(it => Number(it?.discount || 0) > 0)
        const isProforma = inv.isProformaInvoice === true
        const poNumber = inv?.salesOrder?.customerPONumber || inv?.salesOrder?.PODetails
        const billingAdd = (buyer?.Loc && buyer?.Pin)
            ? `${buyer.Loc}`
            : '-';
        const billingPin = buyer?.Pin || '-';
        const shippingAdd =
            `${gstReq?.ShipDtls?.Addr1 || ''}` || '-';
        const shippingPin = ship?.Pin || '-';
        const poDate = inv.salesOrder?.customerPODate
        const soNo = inv.salesOrder?.salesOrderNumber || '-'
        const priceBasis = inv.salesOrder?.priceBasis || '-'
        const irn = inv.irn || inv.gstzenResponse?.Irn || null
        const ackNo = inv.ackNo || inv.gstzenResponse?.AckNo || '-'
        const formatDate = (date) => {
            if (!date || date === '-') return '-';

            return new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        };

        const ackDateRaw = inv.ackDate || inv.gstzenResponse?.AckDt || '-';
        const ackDate = formatDate(ackDateRaw);
        const isIGST = Number(val?.IgstVal || inv?.igstAmount || 0) > 0

        // convert number to ₹ format
        const money = n =>
            (Number(n) || 0).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })

        // Convert number to words (Indian Rupees with Paise)
        const numberToWords = num => {
            const ones = [
                '',
                'One',
                'Two',
                'Three',
                'Four',
                'Five',
                'Six',
                'Seven',
                'Eight',
                'Nine'
            ]
            const teens = [
                'Ten',
                'Eleven',
                'Twelve',
                'Thirteen',
                'Fourteen',
                'Fifteen',
                'Sixteen',
                'Seventeen',
                'Eighteen',
                'Nineteen'
            ]
            const tens = [
                '',
                '',
                'Twenty',
                'Thirty',
                'Forty',
                'Fifty',
                'Sixty',
                'Seventy',
                'Eighty',
                'Ninety'
            ]

            const convertLessThanThousand = n => {
                if (n === 0) return ''
                if (n < 10) return ones[n]
                if (n < 20) return teens[n - 10]
                if (n < 100)
                    return (
                        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
                    )
                return (
                    ones[Math.floor(n / 100)] +
                    ' Hundred' +
                    (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
                )
            }

            // Split into rupees and paise
            const rupees = Math.floor(num)
            const paise = Math.round((num - rupees) * 100)

            if (rupees === 0 && paise === 0) return 'Zero Rupees Only'

            const crore = Math.floor(rupees / 10000000)
            const lakh = Math.floor((rupees % 10000000) / 100000)
            const thousand = Math.floor((rupees % 100000) / 1000)
            const hundred = rupees % 1000

            let result = ''

            if (crore > 0) result += convertLessThanThousand(crore) + ' Crore '
            if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh '
            if (thousand > 0)
                result += convertLessThanThousand(thousand) + ' Thousand '
            if (hundred > 0) result += convertLessThanThousand(hundred)

            // Add "Rupees" part
            let finalResult = result.trim()
            if (finalResult) {
                finalResult += ' Rupees'
            } else {
                finalResult = 'Zero Rupees'
            }

            // Add "Paise" part if present
            if (paise > 0) {
                finalResult += ' and ' + convertLessThanThousand(paise) + ' Paise'
            }

            return finalResult + ' Only'
        }

        // Generate QR Code Image
        let qrImg = ''
        if (inv.gstzenResponse?.SignedQRCode) {
            qrImg = await QRCode.toDataURL(inv.gstzenResponse.SignedQRCode, {
                margin: 0,
                scale: 4
            })
        }

        // Group items by HSN code for GST breakup
        const groupedByHsn = items.reduce((acc, it) => {
            const hsn = it.HsnCd ? String(it.HsnCd).trim() : '-'
            if (!acc[hsn]) {
                acc[hsn] = {
                    hsnCode: hsn,
                    taxableValue: 0,
                    cgstAmt: 0,
                    sgstAmt: 0,
                    igstAmt: 0,
                    gstRate: it.GstRt || 0
                }
            }
            const itemTotal = it.AssAmt
            acc[hsn].taxableValue += itemTotal
            acc[hsn].cgstAmt += Number(it.CgstAmt || 0)
            acc[hsn].sgstAmt += Number(it.SgstAmt || 0)
            acc[hsn].igstAmt += Number(it.IgstAmt || 0)
            return acc
        }, {})

        const hsnGroups = Object.values(groupedByHsn)

        // Use backend-stored total invoice value directly — do NOT recalculate on frontend
        const netTotal = Number(val?.TotInvVal || inv?.totalCreditNoteValue || 0)


        // Helper function to generate header HTML
        const generateHeader = () => `
    <div style="border:1px solid #000; background: white;">
      <!-- Header -->
      <p style="margin:5px 0; font-size:12px; text-align:right">${copy === 'original' ? 'Original for Recipient' : copy === 'duplicate' ? 'Duplicate for Transporter' : copy === 'triplicate' ? 'Triplicate for Supplier' : copy === 'quadruplicate' ? 'Quadruplicate Copy' : ''}</p>
      <div style="text-align:center; padding:10px 20px;">
        <center>
          <h2 style="margin:5px 0">CREDIT NOTE</h2>
          <p style="margin:5px 0; font-size:12px;">${gstReq?.TranDtls?.SupTyp === 'EXPWOP' ? 'SUPPLY MEANT FOR EXPORT UNDER LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX (IGST)' : ''}</p>
        </center>
      </div>
      ${inv && irn
                ? `
                <div style="display:flex; justify-content:space-between; padding:10px; border-top:1px solid #000; border-bottom:1px solid #000">
                    <div style="padding:8px;">
                    <div><b>IRN:</b> ${String(irn)}</div>
                    <div><b>Ack No:</b> ${String(ackNo || '-')}</div>
                    <div><b>Ack Date:</b> ${String(ackDate || '-')}</div>
                </div>  

                <div style="text-align:right">
                    <div>e-Invoice</div>
                    <div style="margin-top:5px; border:1px solid #333; padding:4px">
                        ${qrImg
                    ? `<img src="${qrImg}" width="90" />`
                    : ''
                }
                    </div>
                </div>
            </div>
            `
                : ''
            }
          
      <!-- Seller / Invoice Details -->
      <div style="display:flex; border-bottom:1px solid #000; ${isProforma ? 'border-top:1px solid #000;' : ''
            }">
        <div style="width:40%; padding:8px; border-right:1px solid #000">
          <div style="font-size:12px; font-weight:bold">${String(
                seller.LglNm || '-'
            )}</div>
          <div style="font-size:12px">${String(seller.Addr1 || '')}</div>
          <div style="font-size:12px">GSTIN: ${String(
                seller.Gstin || '-'
            )}</div>
          <div style="font-size:12px">CIN: U74140WB2011PTC159722</div>
          <div style="font-size:12px">UDYAM Registration no: UDYAM-WB-09-0000238</div>
        </div>

        <div style="width:60%; padding:8px">
          <table style="width:100%; border-collapse:collapse">
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Credit Note No</td>
            <td style="padding:2px 4px; font-size:12px">${String(
                gstReq?.DocDtls?.No || '-'
            )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Credit Note Date</td><td style="padding:2px 4px; font-size:12px">${String(
                gstReq?.DocDtls?.Dt || '-'
            )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Customer PO No.</td><td style="padding:2px 4px; font-size:12px">${String(
                poNumber || '-'
            )}</td><tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">
              Customer PO Date
            </td>
            <td style="padding:2px 4px; font-size:12px">
              ${poDate
                ? new Date(poDate).toLocaleDateString('en-GB')
                : '-'
            }
            </td>
          </tr>

            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">SO Id</td><td style="padding:2px 4px; font-size:12px">${String(
                soNo || '-'
            )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Price Basis</td><td style="padding:2px 4px; font-size:12px">${String(
                priceBasis || '-'
            )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Original Invoice</td><td style="padding:2px 4px; font-size:12px">${String(
                inv?.originalInvoice?.invoiceNumber || '-'
            )}</td></tr>
            
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Other References</td><td style="padding:2px 4px; font-size:12px">${String(
                inv.otherReferences || '-'
            )}</td></tr>
          </table>
        </div>
      </div>

      <!-- Consignee / Buyer -->
      <div style="display:flex; border-bottom:1px solid #000">
        
        <div style="width:50%; padding:8px">
          <div style="font-size:12px; font-weight:bold">Bill To</div>
          <div style="font-size:12px">${String(
                buyer.LglNm || '-'
            )}</div>
          <div style="font-size:12px">${String(billingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(billingPin || '-')}</div>
          <div style="font-size:12px">State Code: ${String(
                buyer.Stcd || '-'
            )}</div>
          <div style="font-size:12px">GSTIN: ${String(buyer.Gstin || '-')}</div>
        </div>

        <div style="width:50%; padding:8px; border-left:1px solid #000">
          <div style="font-size:12px; font-weight:bold">Consignee (Ship To)</div>
          <div style="font-size:12px">${String(
                ship.LglNm || '-'
            )}</div>
          <div style="font-size:12px">${String(shippingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(shippingPin || '-')}</div>
          <div style="font-size:12px">State Code: ${String(
                ship.Stcd || '-'
            )}</div>
          <div style="font-size:12px">GSTIN: ${String(ship.Gstin || '-')}</div>
        </div>

      </div>
    </div>
  `

        // Create a temporary container for rendering
        const container = document.createElement('div')
        container.style.cssText = `
      position: absolute;
     left: -9999px;
     top: 0;
     width: 794px;
     padding: 0;
     margin: 0;
     background: white;
     box-sizing: border-box;
    `
        document.body.appendChild(container)

        try {
            // Import jsPDF dynamically if not already available
            const { jsPDF } = window.jspdf || (await import('jspdf'))
            const pdf = new jsPDF('p', 'mm', 'a4')

            const pageHeight = 297 // A4 height in mm
            const pageWidth = 210 // A4 width in mm
            const margin = 5

            // Calculate items per page (estimate based on row height)
            const itemsPerPage = 15 // Adjust based on your needs
            const totalPages = Math.ceil(items.length / itemsPerPage)

            for (let pageNum = 0; pageNum < totalPages; pageNum++) {
                const startIdx = pageNum * itemsPerPage
                const endIdx = Math.min(startIdx + itemsPerPage, items.length)
                const pageItems = items.slice(startIdx, endIdx)
                const isLastPage = pageNum === totalPages - 1

                // Build HTML for this page
                const pageHTML = `
  <div style="font-family: Arial; width: 100%; background: white; padding: 0; margin: 0; box-sizing: border-box;">
    ${generateHeader()}
          
          <!-- Items Table -->
          <div style="border:1px solid #000; border-top:none; padding:0; margin:0;">
            <table style="width:100%; border-collapse:collapse;">
              <thead>
                <tr>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Sl</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Description</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">UOM</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Part Code</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">HSN</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">GST%</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Qty</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Rate</th>
                  ${hasDiscount
                        ? `<th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Disc%</th>`
                        : ``
                    }
                  <th style="padding:8px; font-size:12px; text-align:center; border-bottom:1px solid #000;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${pageItems
                        .map((it, idx) => {
                            const globalIdx = startIdx + idx
                            const narration = pickItems[globalIdx]?.narration || ''
                            const discount = pickItems[globalIdx]?.discount || 0
                            const partCode = pickItems[globalIdx]?.model?.partCode || '-'

                            const productName = narration
                                ? `${String(it.PrdDesc || '-')} <br/> (${String(
                                    narration
                                )})`
                                : String(it.PrdDesc || '-')
                            const baseWithoutDiscount = Number(Math.round(it.UnitPrice) || 0) * Number(it.Qty || 0)
                            const amount =
                                baseWithoutDiscount - ((discount / 100) * baseWithoutDiscount)

                            return `
                      <tr style="${idx === pageItems.length - 1 ? "border-bottom:1px solid #000;" : ""}">
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${globalIdx + 1}</td>
                        <td style="padding:4px; font-size:12px; border-right:1px solid #000;">${productName}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${it.Unit === 'Project' ? 'Nos' : String(it.Unit || '-')}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${partCode}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.HsnCd || '-')}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.GstRt || '-'
                            )}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${it.Qty === 0 ? ' ' : Number(it.Qty || 0)
                                }</td>
                        <td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">
                            ${it.Qty === 0 ? '' : money(it.UnitPrice)}
                        </td>
                        ${hasDiscount
                                    ? `<td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${discount}</td>`
                                    : ``
                                }
                        <td style="padding:4px; font-size:12px; text-align:right;">${money(
                                    it.AssAmt
                                )}</td>
                      </tr>
                    `
                        })
                        .join('')}
                
                ${isLastPage
                        ? `
                <tr>
                  <td colspan="${hasDiscount ? 6 : 5
                        }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Total</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
                            val?.AssVal
                        )}</td>
                </tr>
               ${isIGST
                            ? `<tr>
                  <td colspan="${hasDiscount ? 6 : 5
                            }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">IGST</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
                                val?.IgstVal || 0
                            )}</td>
                </tr>`
                            : `
                <tr>
                  <td colspan="${hasDiscount ? 6 : 5
                            }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">CGST</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
                                val?.CgstVal || inv?.cgstAmount || 0
                            )}</td>
                </tr>
                <tr>
                  <td colspan="${hasDiscount ? 6 : 5
                            }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">SGST</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
                                val?.SgstVal || inv?.sgstAmount || 0
                            )}</td>
                </tr>
                `
                        }
                
                <tr>
                  <td colspan="${hasDiscount ? 6 : 5}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                    Net Total
                  </td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
                            netTotal
                        )}</td>
                  ${(() => {
                            const rndOff = Number(val?.RndOffAmt || 0);
                            if (rndOff === 0) return '';
                            return `
                <td colspan="${hasDiscount ? 6 : 5}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                  Rounded
                </td>
                <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                    ${rndOff > 0 ? '+' : ''}${rndOff.toFixed(2)}
                </td>
              `;
                        })()}
                </tr>
                <tr style="border-top:1px solid #000;">
                  <td colspan="${hasDiscount ? 7 : 6}" style="padding:8px; font-size:11px; font-style:italic">
                    <b>${numberToWords(netTotal)}</b>
                  </td>
                </tr>
                `
                        : ''
                    }
              </tbody>
            </table>
          </div>

          ${isLastPage
                        ? `
          <!-- GST Breakup -->
          <div style="border:1px solid #000; border-top:none; padding:8px;">
            ${isIGST
                            ? `
                <table border="1" style="width:100%; border-collapse:collapse">
                  <tr>
                    <th style="padding:4px; font-size:12px">HSN/SAC</th>
                    <th style="padding:4px; font-size:12px">Taxable Value</th>
                    <th style="padding:4px; font-size:12px">IGST Rate</th>
                    <th style="padding:4px; font-size:12px">IGST Amt</th>
                    <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                  </tr>
                  ${hsnGroups
                                .map(group => {

                                    return `
                    <tr>
                      <td style="padding:4px; font-size:12px">${String(
                                        group.hsnCode
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.taxableValue
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${Number(
                                        group.gstRate || 0
                                    )}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.igstAmt
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.igstAmt
                                    )}</td>
                    </tr>
                  `
                                })
                                .join('')}
                  <tr style="font-weight:bold">
                    <td style="padding:4px; font-size:12px">Total</td>
                    <td style="padding:4px; font-size:12px">${money(
                                    hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0)
                                )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(
                                    hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
                                )}</td>
                    <td style="padding:4px; font-size:12px">${money(
                                    hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
                                )}</td>
                  </tr>
                  <tr>
                    <td colspan="5" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                      <b>${numberToWords(
                                    hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
                                )}</b>
                    </td>
                  </tr>
                </table>
              `
                            : `
                <table border="1" style="width:100%; border-collapse:collapse">
                  <tr>
                    <th style="padding:4px; font-size:12px">HSN/SAC</th>
                    <th style="padding:4px; font-size:12px">Taxable Value</th>
                    <th style="padding:4px; font-size:12px">CGST Rate</th>
                    <th style="padding:4px; font-size:12px">CGST Amt</th>
                    <th style="padding:4px; font-size:12px">SGST Rate</th>
                    <th style="padding:4px; font-size:12px">SGST Amt</th>
                    <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                  </tr>
                  ${hsnGroups
                                .map(group => {
                                    const cgstRate = Number(group.gstRate || 0) / 2;
                                    const sgstRate = Number(group.gstRate || 0) / 2;
                                    return `
                    <tr>
                      <td style="padding:4px; font-size:12px">${String(
                                        group.hsnCode
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.taxableValue
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${cgstRate}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.cgstAmt
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${sgstRate}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.sgstAmt
                                    )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                                        group.cgstAmt + group.sgstAmt
                                    )}</td>
                    </tr>
                  `
                                })
                                .join('')}
                    
                  <tr style="font-weight:bold">
                    <td style="padding:4px; font-size:12px">Total</td>
                    <td style="padding:4px; font-size:12px">${money(
                                    hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0)
                                )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(

                                    hsnGroups.reduce((sum, g) => sum + g.cgstAmt, 0)

                                )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(

                                    hsnGroups.reduce((sum, g) => sum + g.sgstAmt, 0)

                                )}</td>
                    <td style="padding:4px; font-size:12px">${money(

                                    hsnGroups.reduce(
                                        (sum, g) => sum + g.cgstAmt + g.sgstAmt,
                                        0
                                    )

                                )}</td>
                  </tr>
                  <tr>
                    <td colspan="7" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                      <b>${numberToWords(
                                    hsnGroups.reduce((sum, g) => sum + g.cgstAmt + g.sgstAmt, 0)
                                )}</b>
                    </td>
                  </tr>
                  
                </table>
              `
                        }
          </div>
          
          <!-- Declaration -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between">
            <div style="width:48%; border-right:1px solid #000; padding-right:10px;">
              <b style="font-size:12px">Declaration</b><br/>
              <span style="font-size:12px">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</span>
            </div>

            <div style="width:48%; padding-left:10px">
              <b style="font-size:12px">Company's Bank Details</b><br/>
              <span style="font-size:12px">Bank Name: <b>Union Bank of India</b></span><br/>
              <span style="font-size:12px">A/C No: <b>607605010000127</b></span><br/>
              <span style="font-size:12px">Branch & IFSC: <b>Bidhan Nagar Branch & UBIN0560766</b></span>
            </div>
          </div>

          <!-- Signatures -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between">
            <div style="font-size:12px">
             
              Customer Seal and Signature
            </div>

            <div style="text-align:right; font-size:12px">
              
              Authorised Signatory
            </div>
          </div>
          `
                        : ''
                    }
        </div>
      `

                // Render to container
                container.innerHTML = pageHTML

                // Convert to canvas
                const canvas = await html2canvas(container, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                })

                // Add page if not first
                if (pageNum > 0) {
                    pdf.addPage()
                }

                // Add image to PDF
                const imgData = canvas.toDataURL('image/jpeg', 1.0)
                const imgWidth = pageWidth - 2 * margin
                let imgHeight = (canvas.height * imgWidth) / canvas.width

                // Constrain height to fit within page
                const maxHeight = pageHeight - 2 * margin
                if (imgHeight > maxHeight) {
                    imgHeight = maxHeight
                }

                // Position at top of page with proper margin
                pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight)
            }

            // Save PDF
            pdf.save(`invoice-${inv?.invoiceNumber || 'INV'}.pdf`)
        } finally {
            // Clean up
            document.body.removeChild(container)
        }
    }


    const toggleAccordion = id => {
        setOpenId(prev => (prev === id ? '' : id))
    }

    const fetchInvoices = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const params = new URLSearchParams({ pagination: 'false' })
            if (storeid) params.set('storeId', storeid)
            if (debouncedSearch) params.set('search', debouncedSearch)  // ← added

            const url = `${API_URL}credit-notes?${params}`

            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : ''
                },
                redirect: 'follow'
            })

            if (!res.ok) {
                const txt = await res.text()
                throw new Error(txt || `Status ${res.status}`)
            }

            const json = await res.json()
            const docs =
                json?.message?.docs ||
                json?.data?.docs ||
                json?.docs ||
                (Array.isArray(json) ? json : [])

            setInvoices(docs)
        } catch (err) {
            console.error('Error fetching invoices:', err)
            setError(err.message || 'Failed to fetch invoices')
            setInvoices([])
        } finally {
            setLoading(false)
        }
    }, [token, storeid, debouncedSearch])  // ← added debouncedSearch

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    // compute grand total of all invoices
    const summaryTotals = useMemo(() => {
        const total = invoices.reduce(
            (acc, inv) => acc + (Number(inv.totalInvoiceValue) || 0),
            0
        )
        return { total }
    }, [invoices])

    const renderItemLines = invoice => {
        const items =
            invoice?.gstzenRequestPayload?.ItemList &&
                Array.isArray(invoice.gstzenRequestPayload.ItemList)
                ? invoice.gstzenRequestPayload.ItemList
                : []

        if (items.length === 0) {
            return (
                <tr>
                    <td colSpan='11' className='text-center py-3'>
                        No item details available
                    </td>
                </tr>
            )
        }

        return items.map((it, idx) => (
            <tr key={idx}>
                <td>{it.SlNo ?? idx + 1}</td>
                <td>{it.PrdDesc ?? '-'}</td>
                <td>{it.HsnCd ?? '-'}</td>
                <td>{it.Unit ?? '-'}</td>
                <td>{it.Qty ?? 0}</td>
                <td>{money(it.UnitPrice)}</td>
                <td>{money(it.TotAmt)}</td>
                <td>{money(it.Discount)}</td>
                <td>{`${Number(it.GstRt || 0).toFixed(2)}%`}</td>
                <td>{money(it.AssAmt)}</td>
                <td>{money(it.TotItemVal)}</td>
            </tr>
        ))
    }

    const openCancelModal = invoice => {
        const id = invoice._id || invoice.id
        if (!id) return
        setCancelInvoiceId(id)
        setCancelReason('')
        setCancelError(null)
        setCancelModalOpen(true)
    }

    const closeCancelModal = () => {
        if (cancelLoading) return
        setCancelModalOpen(false)
        setCancelInvoiceId(null)
        setCancelReason('')
        setCancelError(null)
    }

    const handleConfirmCancel = async () => {
        if (!cancelInvoiceId) {
            setCancelError('Unable to identify credit note to cancel.')
            return
        }

        if (!cancelReason.trim()) {
            setCancelError('Please enter a reason for cancellation.')
            return
        }

        try {
            setCancelLoading(true)
            setCancelError(null)

            const url = `${API_URL}credit-notes/${cancelInvoiceId}/cancel`

            const res = await fetch(url, {
                method: 'PUT',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reason: cancelReason.trim()
                }),
                redirect: 'follow'
            })

            if (!res.ok) {
                const txt = await res.text()
                throw new Error(txt || `Status ${res.status}`)
            }

            // Optionally parse result if needed
            // const result = await res.json()

            // Refresh invoice list after successful cancellation
            await fetchInvoices()

            setCancelLoading(false)
            closeCancelModal()
        } catch (err) {
            console.error('Error cancelling invoice:', err)
            setCancelLoading(false)
            setCancelError(err.message || 'Failed to cancel invoice')
        }
    }

    const openConvertModal = inv => {
        const id = inv._id || inv.id
        if (!id) return
        setConvertInvoiceId(id)
        setConvertInvoiceNumber(inv.creditNoteNumber || '')
        setConvertError(null)
        setConvertModalOpen(true)
    }

    const closeConvertModal = () => {
        if (convertLoading) return
        setConvertModalOpen(false)
        setConvertInvoiceId(null)
        setConvertInvoiceNumber('')
        setConvertError(null)
    }

    const handleConfirmConvert = async () => {
        if (!convertInvoiceId) {
            setConvertError('Unable to identify credit note to convert.')
            return
        }

        try {
            setConvertLoading(true)
            setConvertError(null)

            // Call the dedicated convert-to-einvoice endpoint
            const url = `${API_URL}credit-notes/${convertInvoiceId}/convert-to-einvoice`

            const res = await fetch(url, {
                method: 'PUT',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                },
                redirect: 'follow'
            })

            const json = await res.json().catch(() => ({}))
            if (!res.ok) {
                const errMsg = json?.message || json?.error || `Status ${res.status}`
                throw new Error(errMsg)
            }

            // Refresh credit note list after successful conversion
            await fetchInvoices()

            setConvertLoading(false)
            closeConvertModal()
            alert('Credit Note successfully converted to E-Invoice!')
        } catch (err) {
            console.error('Error converting to e-invoice:', err)
            setConvertLoading(false)
            setConvertError(err.message || 'Failed to convert to e-invoice')
        }
    }

    /**
     * Open E-way Bill modal for a specific invoice
     * @param {Object} invoice - Invoice object
     */
    const openEwayBillModal = invoice => {
        if (!invoice) return
        setSelectedInvoiceForEwayBill(invoice)
        setEwayBillModalOpen(true)
    }

    /**
     * Close E-way Bill modal and reset state
     */
    const closeEwayBillModal = () => {
        setEwayBillModalOpen(false)
        setSelectedInvoiceForEwayBill(null)
    }

    /**
     * Handle successful E-way Bill generation
     * @param {Object} result - API response result
     */
    const handleEwayBillSuccess = async result => {
        console.log('E-way Bill generated successfully:', result)
        // Refresh invoices to get updated data
        await fetchInvoices()
    }

    /**
     * Sync a credit note to Tally via POST /api/credit-notes/sync
     * Mirrors the handleSyncTally pattern from ViewInvoice.
     */
    const handleSyncCreditNoteTally = async (inv) => {
        const creditNoteNumber = inv?.creditNoteNumber
        if (!creditNoteNumber) {
            alert('Credit note number not found. Cannot sync to Tally.')
            return
        }
        const crnId = String(inv._id || inv.id)
        setSyncTallyState(prev => ({
            ...prev,
            [crnId]: { loading: true, error: null, success: null }
        }))
        try {
            const res = await fetch(`${API_URL}credit-notes/sync`, {
                method: 'POST',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ creditNoteNumber })
            })
            const text = await res.text()
            let message = text
            try { message = JSON.parse(text)?.message || text } catch (_) { }
            if (!res.ok) throw new Error(message || `Status ${res.status}`)
            setSyncTallyState(prev => ({
                ...prev,
                [crnId]: { loading: false, error: null, success: message || 'Synced to Tally successfully!' }
            }))
        } catch (err) {
            console.error('Sync Tally error:', err)
            setSyncTallyState(prev => ({
                ...prev,
                [crnId]: { loading: false, error: err.message || 'Sync failed', success: null }
            }))
        }
    }

    if (loading) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>
                    <Spinner animation='border' size='sm' /> Loading invoices...
                </CardBody>
            </div>
        )
    }

    if (error) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4'>
                    <div className='alert alert-danger mb-0'>Error: {error}</div>
                </CardBody>
            </div>
        )
    }

    if (!invoices || (invoices.length === 0 && !debouncedSearch && !loading)) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>No invoices found.</CardBody>
            </div>
        )
    }

    return (
        <div className='card shadow-sm'>
            <CardBody className='p-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                    <h5 className='mb-0 ml-1'>Credit Notes</h5>
                    <div className='text-muted small'>
                        Total Invoice Value: <strong>₹ {money(summaryTotals.total)}</strong>
                    </div>
                </div>
                <div className='d-flex flex-column flex-sm-row gap-2 mb-3 align-items-sm-center'>
                    <div className='position-relative flex-grow-1'>
                        <input
                            type='text'
                            className='form-control form-control-sm ps-4'
                            placeholder='Search by invoice no., sales order no., company name…'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className='btn btn-link btn-sm position-absolute p-0 text-muted'
                                style={{ right: 8, top: '50%', transform: 'translateY(-50%)' }}
                                onClick={() => setSearch('')}
                            >✕</button>
                        )}
                    </div>
                </div>

                {paginated.length === 0 ? (
                    <div className='text-center text-muted py-5'>
                        {debouncedSearch ? `No invoices match "${debouncedSearch}"` : 'No invoices found.'}
                    </div>
                ) : (
                    <Accordion open={openId || ''} toggle={toggleAccordion} id='viewInvoiceAccordion'>
                        {paginated.map(inv => {
                            const id = String(inv._id || inv.id)

                            const pickList = inv.pickList || {}
                            const salesOrder = inv.salesOrder || {}
                            const di = pickList.deliveryInstruction || {}
                            const gstzenReq = inv.gstzenRequestPayload || {}
                            const buyer = gstzenReq.BuyerDtls || {}
                            const shipping = gstzenReq.ShipDtls || {}
                            const seller = gstzenReq.SellerDtls || {}
                            const val = gstzenReq.ValDtls || {}
                            const so = inv.salesOrder || {}

                            const invoiceNo = inv.creditNoteNumber || inv.invoiceNumber || gstzenReq?.DocDtls?.No || '-'
                            const creditNoteDate =
                                inv.creditNoteDate || gstzenReq?.DocDtls?.Dt || null

                            const status = inv.status || gstzenReq?.IrnStatus || '-'
                            const irnStatus = inv.gstzenResponse?.IrnStatus

                            const totalValue =
                                Number(inv.totalInvoiceValue) || Number(val.TotInvVal) || 0

                            const taxable = Number(inv.taxableValue) || Number(val.AssVal) || 0

                            const igst = Number(inv.igstAmount) || Number(val.IgstVal) || 0
                            const cgst = Number(inv.cgstAmount) || Number(val.CgstVal) || 0
                            const sgst = Number(inv.sgstAmount) || Number(val.SgstVal) || 0

                            const hasError =
                                !!inv.errorMessage || !!inv.gstzenResponse?.ErrorDetails?.length

                            return (
                                <AccordionItem key={id}>
                                    <AccordionHeader targetId={id}>
                                        <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                                            <div className='me-auto'>
                                                <strong></strong> {invoiceNo}{' '}
                                                <span className='text-muted'>
                                                    | SO:{' '}
                                                    {salesOrder.salesOrderNumber ||
                                                        di.salesOrderNumber ||
                                                        '-'}
                                                </span>{' '}
                                                <span className='text-muted'>
                                                    | {buyer.LglNm || '-'}
                                                </span>{' '}
                                                <span className='text-muted'>
                                                    | Box: {pickList.boxNumber || '-'}
                                                </span>
                                            </div>

                                            <div className='text-muted small'>
                                                Credit Note Date: {formatDateTime(creditNoteDate)}
                                            </div>

                                            <div className='ms-md-3 d-flex gap-2 flex-wrap align-items-center'>

                                                <span
                                                    className={`badge ${status === 'active'
                                                        ? 'bg-success'
                                                        : status === 'generated'
                                                            ? 'bg-primary'
                                                            : status === 'cancelled'
                                                                ? 'bg-danger'
                                                                : status === 'failed'
                                                                    ? 'bg-warning text-dark'
                                                                    : 'bg-secondary'
                                                        }`}
                                                >
                                                    Status: {status}
                                                </span>

                                                {irnStatus && (
                                                    <span
                                                        className={`badge ${irnStatus === 'GEN'
                                                            ? 'bg-success'
                                                            : irnStatus === 'ERR'
                                                                ? 'bg-danger'
                                                                : 'bg-secondary'
                                                            }`}
                                                    >
                                                        IRN: {irnStatus}
                                                    </span>
                                                )}

                                                <span className='badge bg-light text-dark'>
                                                    Total: ₹ {money(totalValue)}
                                                </span>

                                                {/* Cancel button */}
                                            </div>
                                        </div>
                                    </AccordionHeader>

                                    <AccordionBody accordionId={id}>
                                        {/* Top summary (Buyer / Seller) */}
                                        <div className='row mb-3'>
                                            <div className='col-md-6'>
                                                <h6>Seller Details</h6>
                                                <div className='small'>
                                                    <div>
                                                        <strong>Name:</strong> {seller.LglNm || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>GSTIN:</strong> {seller.Gstin || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Address:</strong> {seller.Addr1 || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Location:</strong> {seller.Loc || '-'} (
                                                        {seller.Stcd || '-'}) - {seller.Pin || '-'}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='col-md-6'>
                                                <h6>Buyer Details</h6>
                                                <div className='small'>
                                                    <div>
                                                        <strong>Name:</strong> {buyer.LglNm || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>GSTIN:</strong> {buyer.Gstin || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Billing Address:</strong> {buyer.Loc || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Pin:</strong> {buyer.Pin || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Shipping Address:</strong> {shipping.Addr1 || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Pin:</strong> {shipping.Pin || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Location:</strong> {shipping.Loc || '-'} (
                                                        {shipping.Stcd || '-'})
                                                    </div>
                                                    <div>
                                                        <strong>Pin:</strong> {shipping.Pin || '-'}
                                                    </div>
                                                    <div>
                                                        <strong>Place of Supply (POS):</strong>{' '}
                                                        {shipping.Stcd || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Value summary */}
                                        <div className='row mb-3'>
                                            <div className='col-md-12'>
                                                <div className='table-responsive'>
                                                    <Table className='table table-sm mb-0'>
                                                        <tbody>
                                                            <tr>
                                                                <th style={{ width: 200 }}>Taxable Value</th>
                                                                <td>₹ {money(taxable)}</td>
                                                                <th style={{ width: 200 }}>IGST</th>
                                                                <td>₹ {money(igst)}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>CGST</th>
                                                                <td>₹ {money(cgst)}</td>
                                                                <th>SGST</th>
                                                                <td>₹ {money(sgst)}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>Total Invoice Value</th>
                                                                <td colSpan='3'>
                                                                    <strong>₹ {money(totalValue)}</strong>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Error / IRN messages (if any) */}
                                        {hasError && (
                                            <div className='mb-3'>
                                                <div className='alert alert-warning small mb-1'>
                                                    <strong>GST Portal Errors:</strong>
                                                </div>
                                                <pre
                                                    className='small bg-light p-2 rounded'
                                                    style={{ whiteSpace: 'pre-wrap' }}
                                                >
                                                    {inv.errorMessage || inv.gstzenResponse?.message || ''}
                                                </pre>

                                                {inv.gstzenResponse?.ErrorDetails &&
                                                    inv.gstzenResponse.ErrorDetails.length > 0 && (
                                                        <ul className='small mb-0'>
                                                            {inv.gstzenResponse.ErrorDetails.map((e, i) => (
                                                                <li key={i}>
                                                                    [{e.ErrorCode}] {e.ErrorMessage}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                            </div>
                                        )}

                                        {/* Item table */}
                                        <div className='table-responsive'>
                                            <Table
                                                className='table table-sm table-bordered mb-0'
                                                responsive
                                            >
                                                <thead className='table-light'>
                                                    <tr>
                                                        <th style={{ width: 50 }}>Sl</th>
                                                        <th>Description</th>
                                                        <th style={{ width: 120 }}>HSN</th>
                                                        <th style={{ width: 80 }}>UOM</th>
                                                        <th style={{ width: 80 }}>Qty</th>
                                                        <th style={{ width: 120 }}>Unit Price</th>
                                                        <th style={{ width: 120 }}>Gross Amt</th>
                                                        <th style={{ width: 120 }}>Discount</th>
                                                        <th style={{ width: 80 }}>GST %</th>
                                                        <th style={{ width: 130 }}>Taxable</th>
                                                        <th style={{ width: 130 }}>Line Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>{renderItemLines(inv)}</tbody>
                                            </Table>
                                        </div>
                                        {(inv?.gstzenRequestPayload?.ItemList || []).length > 0 && (
                                            <div className='flex justify-end mt-2 gap-2'>
                                                {/* Sync to Tally button */}
                                                {(() => {
                                                    const crnId = String(inv._id || inv.id)
                                                    const syncState = syncTallyState[crnId] || {}
                                                    return (
                                                        <>
                                                            <button
                                                                className='btn btn-danger'
                                                                disabled={syncState.loading}
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleSyncCreditNoteTally(inv)
                                                                }}
                                                                title='Sync this credit note to Tally'
                                                            >
                                                                {syncState.loading ? (
                                                                    <><span className='spinner-border spinner-border-sm me-1' role='status' aria-hidden='true'></span>Syncing…</>
                                                                ) : (
                                                                    <>Sync To Tally</>
                                                                )}
                                                            </button>
                                                            {syncState.success && (
                                                                <span className='text-success small align-self-center'>
                                                                    ✅ {syncState.success}
                                                                </span>
                                                            )}
                                                            {syncState.error && (
                                                                <span className='text-danger small align-self-center'>
                                                                    ❌ {syncState.error}
                                                                </span>
                                                            )}
                                                        </>
                                                    )
                                                })()}

                                                {/* E-Invoice button — only for active (non-e-invoice) CRNs */}
                                                {inv.status === 'active' && !inv.isEInvoice && (
                                                    <button
                                                        className='btn btn-success'
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            openConvertModal(inv)
                                                        }}
                                                    >
                                                        E-Invoice
                                                    </button>
                                                )}

                                                {status === 'generated' && (
                                                    <button
                                                        className='btn btn-danger'
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            openCancelModal(inv)
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                )}

                                                <button
                                                    className='btn add-btn text-white'
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        handleDownloadCRN(inv, '')
                                                    }}
                                                >
                                                    Print
                                                </button>
                                            </div>
                                        )}
                                    </AccordionBody>
                                </AccordionItem>
                            )
                        })}
                    </Accordion>
                )}

                {totalPages > 1 && (
                    <div className='d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2'>
                        <div className='text-muted small'>
                            Showing {Math.min((page - 1) * pageSize + 1, invoices.length)}–
                            {Math.min(page * pageSize, invoices.length)} of {invoices.length}
                        </div>
                        <nav>
                            <ul className='pagination pagination-sm mb-0'>
                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                    <button className='page-link' onClick={() => setPage(1)}>«</button>
                                </li>
                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                    <button className='page-link' onClick={() => setPage(p => p - 1)}>‹</button>
                                </li>
                                {pageNums()[0] > 1 && (
                                    <li className='page-item disabled'><span className='page-link'>…</span></li>
                                )}
                                {pageNums().map(n => (
                                    <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                                        <button className='page-link' onClick={() => setPage(n)}>{n}</button>
                                    </li>
                                ))}
                                {pageNums().at(-1) < totalPages && (
                                    <li className='page-item disabled'><span className='page-link'>…</span></li>
                                )}
                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                    <button className='page-link' onClick={() => setPage(p => p + 1)}>›</button>
                                </li>
                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                    <button className='page-link' onClick={() => setPage(totalPages)}>»</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}

                {/* Cancel Invoice Modal */}
                <Modal
                    isOpen={cancelModalOpen}
                    toggle={closeCancelModal}
                    size='lg'
                    centered
                >
                    <ModalHeader toggle={closeCancelModal}>Cancel Invoice</ModalHeader>
                    <ModalBody>
                        <div className='mb-3'>
                            <label className='form-label'>
                                Reason for cancellation <span className='text-danger'>*</span>
                            </label>
                            <textarea
                                className='form-control'
                                rows='3'
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                                placeholder='Enter reason for cancelling this invoice'
                            />
                        </div>
                        {cancelError && (
                            <div className='alert alert-danger py-1 small mb-0'>
                                {cancelError}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color='secondary'
                            onClick={closeCancelModal}
                            disabled={cancelLoading}
                        >
                            Close
                        </Button>
                        <Button
                            color='danger'
                            onClick={handleConfirmCancel}
                            disabled={cancelLoading}
                        >
                            {cancelLoading ? (
                                <>
                                    <Spinner animation='border' size='sm' /> Cancelling...
                                </>
                            ) : (
                                'Confirm Cancel'
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Convert to E-Invoice Modal */}
                <Modal
                    isOpen={convertModalOpen}
                    toggle={closeConvertModal}
                    size='lg'
                    centered
                >
                    <ModalHeader toggle={closeConvertModal}>
                        Convert Credit Note to E-Invoice
                    </ModalHeader>
                    <ModalBody>
                        <p className='mb-2'>
                            You are about to submit Credit Note{' '}
                            <strong>{convertInvoiceNumber || '-'}</strong>{' '}
                            to the GST portal to generate an IRN (e-invoice).
                        </p>
                        <p className='text-secondary small mb-0'>
                            This action is irreversible. Once the IRN is generated the CRN
                            status will change to <strong>generated</strong>.
                        </p>
                        {convertError && (
                            <div className='alert alert-danger py-1 small mt-3 mb-0'>
                                {convertError}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color='secondary'
                            onClick={closeConvertModal}
                            disabled={convertLoading}
                        >
                            Close
                        </Button>
                        <Button
                            color='success'
                            onClick={handleConfirmConvert}
                            disabled={convertLoading}
                        >
                            {convertLoading ? (
                                <>
                                    <Spinner animation='border' size='sm' /> Converting...
                                </>
                            ) : (
                                'Convert to E-Invoice'
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* E-way Bill Modal */}
                <CreateEwayBillModal
                    isOpen={ewayBillModalOpen}
                    toggle={closeEwayBillModal}
                    invoice={selectedInvoiceForEwayBill}
                    onSuccess={handleEwayBillSuccess}
                />
            </CardBody>
        </div>
    )
}

export default ViewCRN
